import React from "react";
import { PDFDownloadLink, pdf } from "@react-pdf/renderer";
import ReceiptPDF from "./ReceiptPDF";
import { getFormattedTransactionId } from "utils/utilityFunctions";
import { useOrganizationLogo } from "utils/organizationLogoCache";

const getStoredOrganization = () => {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(localStorage.getItem("organization") || "{}");
  } catch {
    return {};
  }
};

const ReceiptDownloadButton = ({
  transaction,
  type,
  buttonText = "View Receipt",
  loadingText = "Generating receipt...",
  className = "",
  onClick,
  variant = "button",
}) => {
  // This ensures the component only renders on client side
  const [isClient, setIsClient] = React.useState(false);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const organization = React.useMemo(
    () => (isClient ? getStoredOrganization() : {}),
    [isClient],
  );
  const {
    logoSrc: organizationLogoSrc,
    isLogoLoading,
    logoUrl: organizationLogoUrl,
  } = useOrganizationLogo(organization, {
    output: "dataUrl",
    fallbackWhileLoading: false,
  });
  const isLogoReady = !organizationLogoUrl || !isLogoLoading || Boolean(organizationLogoSrc);

  React.useEffect(() => {
    setIsClient(true);
  }, []);

  // Generate a useful filename based on transaction type and data
  const generateFilename = (formattedId) => {
    let prefix = "";

    switch (type) {
      case "material":
        prefix = "Material_Purchase";
        break;
      case "worker":
        prefix = "Worker_Payment";
        break;
      case "received":
        prefix = "Received_Payment";
        break;
      case "vendor":
        prefix = "Vendor_Payment";
        break;
      case "contractor":
        prefix = "Contractor_Payment";
        break;
      case "refund":
        prefix = "Refund";
        break;
      default:
        prefix = "Transaction";
    }

    return `${prefix}_Receipt_${formattedId}.pdf`;
  };

  // FIX: Merge top-level transaction fields with originalTransaction so
  // ReceiptPDF can access fields like desc, medium, project, etc.
  // Top-level fields (desc, medium, id...) take priority over originalTransaction fields.
  const mergedTransactionData = {
    ...(transaction.originalTransaction || {}),
    ...transaction,
  };
  const formattedId =
    mergedTransactionData?.receipt_id ||
    mergedTransactionData?.transaction_id ||
    getFormattedTransactionId(mergedTransactionData, type);
  const safeFormattedId = String(formattedId || '').replace(/[\\/:"*?<>|]+/g, "-");

  const baseClassName =
    variant === "menuItem"
      ? "w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-gray-800 transition-colors hover:bg-gray-50"
      : "px-3 py-1.5 text-sm bg-purple-100 text-purple-800 rounded-lg hover:bg-purple-200 flex items-center gap-2";
  const mergedClassName = `${baseClassName} ${className}`.trim();
  const receiptDocument = (
    <ReceiptPDF
      transactionData={mergedTransactionData}
      type={type}
      organizationLogoSrc={organizationLogoSrc}
    />
  );
  const fileName = generateFilename(safeFormattedId);

  const handleMenuDownload = async (event) => {
    event?.stopPropagation();
    if (isGenerating) return;

    let shouldCloseMenu = false;
    try {
      setIsGenerating(true);
      const blob = await pdf(receiptDocument).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      shouldCloseMenu = true;
    } catch (error) {
      console.error("Failed to download receipt:", error);
    } finally {
      setIsGenerating(false);
    }

    if (shouldCloseMenu && typeof onClick === "function") {
      onClick(event);
    }
  };

  if (variant === "menuItem") {
    return (
      <button
        type="button"
        className={mergedClassName}
        disabled={!isClient || isGenerating || !isLogoReady}
        onClick={handleMenuDownload}
      >
        {isGenerating || (isClient && !isLogoReady) ? loadingText : buttonText}
      </button>
    );
  }

  return isClient && isLogoReady ? (
    <PDFDownloadLink
      document={receiptDocument}
      fileName={fileName}
      className={mergedClassName}
      onClick={onClick}
    >
      {({ blob, url, loading, error }) =>
        loading ? loadingText : buttonText
      }
    </PDFDownloadLink>
  ) : (
    <button
      className={mergedClassName}
      disabled
      onClick={onClick}
    >
      {isClient && !isLogoReady ? loadingText : buttonText}
    </button>
  );
};

export default ReceiptDownloadButton;
